'use client';

import {useActionState} from 'react';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';

import {Alert, AlertBody, AlertHeading} from '@/modules/foundation/components/alert';
import {Breadcrumb} from '@/modules/foundation/components/breadcrumb';
import {Button} from '@/modules/foundation/components/button';
import {Container} from '@/modules/foundation/components/container';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {PortableText} from '@/modules/foundation/sanity/portable-text';
import {TextHeading} from '@/modules/page/components/page-head/text-heading';

import {addToCart, updateCart} from '../../cart/actions';
import {EmbroideryOptionSelector} from '../../embroidery-collection/EmbroideryOptionSelector';
import {FormattedItemPrice} from '../../item/FormattedItemPrice';
import type {ItemWithVatTypeFragment} from '../../item/query';
import {
	productConfigurationFromSearchParams,
	searchParamsFromProductConfiguration,
} from '../configuration';
import {ProductImageCarousel} from '../ProductImageCarousel';
import {PRODUCT_PAGE_ALL_SLUG} from '../schema/constants';

import {CabFloorMatEdgingColorSelector} from './edging/CabFloorMatEdgingColorSelector';
import {CabFloorMatMaterialSelector} from './material/CabFloorMatMaterialSelector';
import {
	cabFloorMatConfigurationFromFormData,
	getDefaultCabFloorMatConfiguration,
	getFilteredEmbroideryOptionForCabFloorMat,
	getItemsForCabFloorMatConfiguration,
	validateCabFloorMatConfiguration,
} from './configuration';
import type {CabFloorMatProductFragment} from './query';

export type CabFloorMatProductPageProps = {
	product: CabFloorMatProductFragment;
	shoppingDisabled?: boolean;
};

export default function CabFloorMatProductPage({
	product,
	shoppingDisabled,
}: CabFloorMatProductPageProps) {
	const searchParams = useSearchParams();
	const router = useRouter();
	const pathname = usePathname();
	const [addToCartState, addToCartAction, isAddingToCart] = useActionState(addToCart, {});
	const [updateCartState, updateCartAction, isUpdatingCart] = useActionState(updateCart, {});

	const cartLineId = searchParams.get('cartLineId') ?? null;

	const configuration =
		productConfigurationFromSearchParams(product, searchParams) ??
		getDefaultCabFloorMatConfiguration(product);
	const validation = validateCabFloorMatConfiguration(product, configuration);
	const isValid = validation?.isValid;

	// Find selected items:
	let selectedItems: ItemWithVatTypeFragment[] = [];
	if (isValid) {
		selectedItems = getItemsForCabFloorMatConfiguration(product, configuration);
	}

	const category = product.category;
	const subcategory = product.subcategory;

	const productSlug = `${subcategory?.fullSlug}/${product.slug?.current}`;

	// Get the brand ids of the product and its compatible vehicles. We only want to show
	// embroidery options for these brands.
	const embroideryOption = getFilteredEmbroideryOptionForCabFloorMat(product);

	// FIXME: need to check out how to use product image with logo if it's available
	// FIXME: products needs to have description
	// FIXME: Move this to the generateMetadata function@
	/*
	const productData: WithContext<Product> = {
		'@context': 'https://schema.org',
		'@type': 'Product',
		name: product.name,
		brand: product.resolvedBrand?.name ?? undefined,
		category: product.subcategory?.name ?? undefined,
		offers: {
			'@type': 'Offer',
			price: priceIncVat.toString(),
			priceCurrency: 'NOK',
			itemCondition: 'https://schema.org/NewCondition',
			url: `${siteUrl}/${productSlug}`,
		},
	};
	*/

	return (
		<>
			<TextHeading
				heading={product.name}
				breadcrumb={
					<Breadcrumb
						breadcrumb={[
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}`,
								title: 'Produkter',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${category?.slug}`,
								title: category?.name ?? '',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${subcategory?.fullSlug}`,
								title: subcategory?.name ?? '',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${productSlug}`,
								title: product.name ?? '',
								active: true,
							},
						]}
					/>
				}
			>
				<Paragraph size="large">{product.supplier?.name}</Paragraph>
			</TextHeading>
			<Container width="normal" spacingBottom="normal" spacingTop="small">
				<div className="md:flex md:flex-row md:items-start md:gap-8">
					{/*<JsonLd jsonLd={productData} />*/}

					<div className="mb-8 md:sticky md:top-8 md:w-1/2">
						<ProductImageCarousel product={product} config={configuration} />
					</div>

					<form
						className="md:w-1/2"
						action={cartLineId ? updateCartAction : addToCartAction}
						onChange={(e) => {
							// TODO: Debounce these events to avoid many consecutive reloads

							// Update the page search params with the new configuration:
							const formData = new FormData(e.currentTarget);
							const configuration = cabFloorMatConfigurationFromFormData(
								product,
								formData,
							);

							if (configuration) {
								const params = searchParamsFromProductConfiguration(configuration);
								if (cartLineId) {
									params.set('cartLineId', cartLineId);
								}

								router.replace(`${pathname}?${params.toString()}`, {
									scroll: false,
								});
							}
						}}
					>
						<input type="hidden" name="productId" value={product._id} />
						{cartLineId?.length ? (
							<input
								name="cartLineId"
								defaultValue={cartLineId}
								className="sr-only"
							/>
						) : null}

						{product.description && (
							<div className="mb-8">
								<PortableText value={product.description} />
							</div>
						)}

						<CabFloorMatMaterialSelector
							className="mb-8"
							namePrefix="material"
							materials={product.settings?.materials ?? []}
							defaultConfiguration={configuration.material}
							validationError={validation.materialError}
						/>

						{(embroideryOption?.available ||
							product.settings?.edgingColor?.edgingColors?.length) && (
							<>
								<Heading level={2} size="h3" className="mb-2">
									Tilpasninger
								</Heading>
								<div className="mb-8">
									{embroideryOption?.available && (
										<EmbroideryOptionSelector
											legend="Broderi"
											namePrefix="embroidery"
											option={embroideryOption}
											defaultConfiguration={configuration.embroidery}
											className="mb-4"
										/>
									)}

									{product.settings?.edgingColor?.edgingColors?.length && (
										<CabFloorMatEdgingColorSelector
											legend="Tilpasset kantfarge"
											namePrefix="edgingColor"
											defaultConfiguration={configuration.edgingColor ?? null}
											option={product.settings.edgingColor}
										/>
									)}
								</div>
							</>
						)}

						{selectedItems && !!selectedItems.length && (
							<div className="text-primary font-display mb-8 text-4xl">
								<h2 className="sr-only">Pris</h2>
								<FormattedItemPrice items={selectedItems} />
							</div>
						)}

						{!validation?.isValid && (
							<Alert severity="warning" className="mb-8">
								<AlertHeading>Ugyldig konfigurasjon</AlertHeading>
								<AlertBody>
									Valgt konfigurasjon er ikke gyldig og kan ikke legges i
									handlekurven. Vennligst se over feilmeldingene over og gjør
									nødvendige endringer.
								</AlertBody>
							</Alert>
						)}
						{(addToCartState.error || updateCartState.error) && (
							<Alert severity="danger" className="mb-8">
								<AlertHeading>Feil med handlekurv</AlertHeading>
								<AlertBody className="mb-2">
									Noe gikk galt ved oppdatering av handlekurven. Vennligst prøv
									igjen, men kontakt oss hvis problemet vedvarer.
								</AlertBody>
								<AlertBody>
									Systemet sa{' '}
									<strong>
										&laquo;
										{addToCartState.error}
										{updateCartState.error}&raquo;
									</strong>
									.
									<br />
								</AlertBody>
							</Alert>
						)}
						<Button
							type="submit"
							disabled={
								shoppingDisabled || !isValid || isAddingToCart || isUpdatingCart
							}
							size="lg"
						>
							{isAddingToCart || isUpdatingCart
								? cartLineId
									? 'Oppdaterer handlevognen'
									: 'Legger til i handlekurv...'
								: cartLineId
									? 'Oppdater handlevognen'
									: 'Legg i handlekurv'}
						</Button>
						{shoppingDisabled && (
							<Paragraph className="mt-4">
								Netthandel er midlertidig deaktivert. Sjekk tilbake senere.
							</Paragraph>
						)}
					</form>
				</div>
			</Container>
		</>
	);
}
